// Users: signups over time, paid vs free, weekly retention cohort heatmap.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { AdminTestUsersCard } from "@/components/admin/AdminTestUsersCard";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

type Profile = { user_id: string; created_at: string; credits_purchased_total: number };
type TryonRow = { user_id: string; created_at: string };

export default function AdminUsers() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [tryons, setTryons] = useState<TryonRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const since = new Date(Date.now() - 60 * 86400_000).toISOString();
      const [p, t] = await Promise.all([
        supabase
          .from("profiles")
          .select("user_id,created_at,credits_purchased_total")
          .gte("created_at", since)
          .limit(10000),
        supabase
          .from("tryons")
          .select("user_id,created_at")
          .gte("created_at", since)
          .limit(10000),
      ]);
      if (!active) return;
      setProfiles((p.data ?? []) as Profile[]);
      setTryons((t.data ?? []) as TryonRow[]);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  const signupsByDay = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of profiles) m.set(p.created_at.slice(0, 10), (m.get(p.created_at.slice(0, 10)) ?? 0) + 1);
    return Array.from(m.entries())
      .map(([day, count]) => ({ day, count }))
      .sort((a, b) => a.day.localeCompare(b.day));
  }, [profiles]);

  const paidStats = useMemo(() => {
    const paid = profiles.filter((p) => Number(p.credits_purchased_total ?? 0) > 0).length;
    return { paid, free: profiles.length - paid, total: profiles.length };
  }, [profiles]);

  // Weekly retention cohort heatmap: cohort = ISO week of signup, return cells = % of cohort that
  // ran a try-on N weeks later.
  const cohort = useMemo(() => {
    const weekOf = (iso: string) => {
      const d = new Date(iso);
      const tmp = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
      tmp.setUTCDate(tmp.getUTCDate() - tmp.getUTCDay());
      return tmp.toISOString().slice(0, 10);
    };
    const userSignupWeek = new Map<string, string>();
    const cohortSize = new Map<string, number>();
    for (const p of profiles) {
      const w = weekOf(p.created_at);
      userSignupWeek.set(p.user_id, w);
      cohortSize.set(w, (cohortSize.get(w) ?? 0) + 1);
    }
    // For each user, find which week N they returned in via tryons.
    const cell = new Map<string, Map<number, Set<string>>>();
    for (const t of tryons) {
      const sw = userSignupWeek.get(t.user_id);
      if (!sw) continue;
      const tw = weekOf(t.created_at);
      const weeksOut = Math.round(
        (new Date(tw).getTime() - new Date(sw).getTime()) / (7 * 86400_000),
      );
      if (weeksOut < 0 || weeksOut > 7) continue;
      if (!cell.has(sw)) cell.set(sw, new Map());
      const row = cell.get(sw)!;
      if (!row.has(weeksOut)) row.set(weeksOut, new Set());
      row.get(weeksOut)!.add(t.user_id);
    }
    const cohorts = Array.from(cohortSize.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .slice(0, 8);
    return cohorts.map(([week, size]) => ({
      week,
      size,
      weeks: Array.from({ length: 8 }, (_, n) => {
        const set = cell.get(week)?.get(n);
        return set ? set.size / size : 0;
      }),
    }));
  }, [profiles, tryons]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Users</h1>
        <p className="text-sm text-muted-foreground">Last 60 days · signups, paid split, retention.</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Kpi label="Signups (60d)" value={paidStats.total.toLocaleString()} />
        <Kpi label="Paid users" value={paidStats.paid.toLocaleString()} sub={`${((paidStats.paid / Math.max(1, paidStats.total)) * 100).toFixed(1)}% conv`} />
        <Kpi label="Free users" value={paidStats.free.toLocaleString()} />
      </div>

      <AdminTestUsersCard />


      <Card>
        <CardHeader>
          <CardTitle className="text-base">Daily signups</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[240px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={signupsByDay}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Area type="monotone" dataKey="count" stroke="hsl(var(--primary))" fill="hsl(var(--primary)/0.2)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Weekly retention cohorts</CardTitle>
        </CardHeader>
        <CardContent>
          {cohort.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">Not enough data yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="pb-2 pr-3">Cohort</th>
                    <th className="pb-2 text-right">Size</th>
                    {Array.from({ length: 8 }, (_, i) => (
                      <th key={i} className="pb-2 text-center">W{i}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cohort.map((row) => (
                    <tr key={row.week} className="border-t">
                      <td className="py-2 pr-3 font-mono text-[10px]">{row.week}</td>
                      <td className="py-2 text-right tabular-nums">{row.size}</td>
                      {row.weeks.map((pct, i) => (
                        <td key={i} className="py-2 text-center">
                          <span
                            className="inline-block min-w-[40px] rounded px-2 py-0.5 text-[10px] tabular-nums"
                            style={{
                              backgroundColor: `hsl(var(--primary) / ${Math.min(0.7, pct).toFixed(2)})`,
                              color: pct > 0.3 ? "hsl(var(--primary-foreground))" : undefined,
                            }}
                          >
                            {pct > 0 ? `${(pct * 100).toFixed(0)}%` : "—"}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
        {sub ? <div className="mt-0.5 text-[10px] text-muted-foreground">{sub}</div> : null}
      </CardContent>
    </Card>
  );
}
